import { InitialalsAvatar } from '@/components/displays/InitialsAvatar';
import { NO_BREAK_SPACE } from '@/constant';
import { useSlideResults } from '@/hooks/useSlideResults/useSlideResults';
import { useStudentResults } from '@/hooks/useStudentResults/useStudentResults';
import { EditorProvider } from '@/providers/editor/EditorProvider';
import { generateAvatarColor, getInitials } from '@/utils/avatar';
import { useQuery } from '@apollo/client';
import FaceIcon from '@mui/icons-material/Face';
import HelpIcon from '@mui/icons-material/Help';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import NoteIcon from '@mui/icons-material/Note';
import PieChartIcon from '@mui/icons-material/PieChart';
import PreviewIcon from '@mui/icons-material/Preview';
import TimerIcon from '@mui/icons-material/Timer';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, IconButton, Skeleton, Stack, Tab, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, getGridStringOperators } from '@mui/x-data-grid';
import { PageHeader, useNotifications } from '@toolpad/core';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { SlideResult, StudentResult } from '../../hooks/types/result';
import { ResultTabPanel } from './components/ResultTabPanel';
import { ResultsPageContainer } from './components/ResultsPageContainer';
import { ResultsPageContent } from './components/ResultsPageContent';
import { ResultsSummaryCard } from './components/ResultsSummaryCard';
import { SlideResultsDialog } from './components/SlideResultsDialog';
import { StudentResultsDialog } from './components/StudentResultsDialog';
import { formatDuration } from './format';
import { GET_HOMEWORK, GetHomeworkResponse, GetHomeworkVariables } from './gql';

function ResultPageHeader({ name }: { name?: string }) {
    const { t } = useTranslation();

    const breadcrumbs = [{ title: t('result.title') ?? '', path: '/results' }, { title: name ?? '' }];
    // non-breaking space when title is empty to keep the component's height
    return <PageHeader title={name ?? NO_BREAK_SPACE} breadcrumbs={breadcrumbs} />;
}

function SummaryTab({ homeworkId }: { homeworkId: string }) {
    const { t } = useTranslation();
    const { results, totalQuestionCount, loading } = useStudentResults({ type: 'homework', id: homeworkId });

    const studentResults = useMemo(() => Object.values(results), [results]);
    const studentCount = useMemo(() => studentResults.length, [studentResults.length]);
    const averageScore = useMemo(() => (studentCount === 0 ? 0 : studentResults.reduce((acc, result) => acc + result.score, 0) / studentCount), [studentCount, studentResults]);
    const averageTimeSpent = useMemo(() => (studentCount === 0 ? 0 : studentResults.reduce((acc, result) => acc + result.timeSpent, 0) / studentCount), [studentCount, studentResults]);

    const size = { xs: 12, sm: 6, md: 4, lg: 3 };

    const CustomSkeleton = useCallback(() => {
        return <Skeleton variant='rounded' sx={{ height: 90 }} />;
    }, []);

    return (
        <Grid
            container
            rowSpacing={1}
            columnSpacing={1}
            sx={(theme) => ({
                height: '100%',
                pt: 2,
                px: 1,
                border: 1,

                borderTop: 0,
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
                borderColor: (theme.vars || theme).palette.divider,
            })}
        >
            <Grid size={size}>{loading ? <CustomSkeleton /> : <ResultsSummaryCard icon={<FaceIcon />} title={t('result.label.students')} value={studentCount.toString()} />}</Grid>
            <Grid size={size}>{loading ? <CustomSkeleton /> : <ResultsSummaryCard icon={<HelpIcon />} title={t('result.label.questions')} value={totalQuestionCount.toString()} />}</Grid>
            <Grid size={size}>
                {loading ? (
                    <CustomSkeleton />
                ) : (
                    <ResultsSummaryCard icon={<PieChartIcon />} title={t('result.label.averageScore')} value={averageScore.toFixed(1).toString() + ' / ' + totalQuestionCount.toString()} />
                )}
            </Grid>
            <Grid size={size}>
                {loading ? <CustomSkeleton /> : <ResultsSummaryCard icon={<TimerIcon />} title={t('result.label.averageTimeSpent')} value={formatDuration(averageTimeSpent / 1000)} />}
            </Grid>
        </Grid>
    );
}

function StudentTab({ homeworkId, onViewResult }: { homeworkId: string; onViewResult?: (data: StudentResult) => void }) {
    const { t } = useTranslation();
    const { results, loading } = useStudentResults({ type: 'homework', id: homeworkId });

    const columns: GridColDef[] = React.useMemo(
        () => [
            {
                field: 'studentName',
                headerName: t('student.label.name'),
                sortable: true,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 2,
                renderCell: (params) => {
                    const name = params.value || '';
                    const avatarColor = generateAvatarColor(name);
                    const bg = avatarColor?.bg;
                    const text = avatarColor?.text;

                    return (
                        <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                            <InitialalsAvatar
                                sx={{
                                    bgcolor: bg,
                                    color: text,
                                }}
                            >
                                {getInitials(name)}
                            </InitialalsAvatar>
                            <Typography variant='body2'>{name}</Typography>
                        </Stack>
                    );
                },
            },
            {
                field: 'studentNumber',
                headerName: t('student.label.number'),
                sortable: true,
                filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains'),
                minWidth: 160,
                flex: 1,
            },
            {
                field: 'score',
                headerName: t('result.label.score'),
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                minWidth: 160,
                flex: 1,
                valueGetter: (value, row) => {
                    return `${value} / ${row.questionCount}`;
                },
            },
            {
                field: 'timeSpent',
                headerName: t('result.label.timeSpent'),
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                minWidth: 160,
                flex: 1,
                renderCell: (params) => {
                    return formatDuration(params.value / 1000);
                },
            },
            {
                field: 'actions',
                headerName: t('common.actions'),
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderCell: () => {
                    return (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <IconButton size='small'>
                                <PreviewIcon />
                            </IconButton>
                        </Box>
                    );
                },
            },
        ],
        [t],
    );

    return (
        <DataGrid
            columns={columns}
            rows={Object.values(results).sort((a, b) => a.studentName.localeCompare(b.studentName))}
            loading={loading}
            onRowClick={(params) => {
                onViewResult?.(params.row);
            }}
            sx={{
                // disable header focus style
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none',
                },
                // disable cell selection style
                '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                },
                // pointer cursor on ALL rows
                '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                },
                '& .MuiDataGrid-columnHeader': {
                    borderRadius: '0px !important',
                },
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
            }}
            autoPageSize
            hideFooterSelectedRowCount
            slotProps={{
                loadingOverlay: {
                    variant: 'linear-progress',
                    noRowsVariant: 'linear-progress',
                },
            }}
        />
    );
}

function SlideTab({ homeworkId, onViewResult }: { homeworkId: string; onViewResult?: (data: SlideResult & { index: number }) => void }) {
    const { t } = useTranslation();
    const { results, loading } = useSlideResults({ type: 'homework', id: homeworkId });

    const sortedResults = React.useMemo(() => {
        return Object.values(results).sort((a, b) => a.sort - b.sort);
    }, [results]);

    const columns: GridColDef<SlideResult>[] = React.useMemo(
        () => [
            {
                field: 'slideName',
                headerName: t('result.label.slide'),
                sortable: false,
                filterable: false,
                minWidth: 160,
                flex: 2,
                renderCell: (params) => {
                    return `${t('result.label.slide')} (${sortedResults.findIndex((result) => result.id === params.row.id) + 1})`;
                },
            },
            {
                field: 'averageScore',
                headerName: t('result.label.averageScore'),
                sortable: true,
                filterable: false,
                minWidth: 160,
                flex: 1,
                renderCell: (params) => {
                    return `${params.value.toFixed(1)} / ${params.row.questionCount}`;
                },
            },
            {
                field: 'averageTimeSpent',
                headerName: t('result.label.averageTimeSpent'),
                sortable: true,
                filterable: false,
                minWidth: 160,
                flex: 1,
                valueGetter: (value) => {
                    return formatDuration(value / 1000);
                },
            },
            {
                field: 'actions',
                headerName: t('common.actions'),
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderCell: () => {
                    return (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <IconButton size='small'>
                                <PreviewIcon />
                            </IconButton>
                        </Box>
                    );
                },
            },
        ],
        [sortedResults, t],
    );

    return (
        <DataGrid
            columns={columns}
            rows={sortedResults}
            loading={loading}
            onRowClick={(params) => {
                onViewResult?.({ ...params.row, index: sortedResults.findIndex((result) => result.id === params.row.id) });
            }}
            sx={{
                // disable header focus style
                '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none',
                },
                // disable cell selection style
                '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none',
                },
                // pointer cursor on ALL rows
                '& .MuiDataGrid-row:hover': {
                    cursor: 'pointer',
                },
                '& .MuiDataGrid-columnHeader': {
                    borderRadius: '0px !important',
                },
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
            }}
            autoPageSize
            hideFooterSelectedRowCount
            slotProps={{
                loadingOverlay: {
                    variant: 'linear-progress',
                    noRowsVariant: 'linear-progress',
                },
            }}
        />
    );
}

export default function ResultsClassroomPage() {
    // provider
    const params = useParams();
    const { t } = useTranslation();
    const notifications = useNotifications();

    const homeworkId = params.homeworkId;
    if (!homeworkId) {
        notifications.show('Homework ID is required', { severity: 'error' });
        throw new Error('Homework ID is required');
    }

    // initial state
    const initTab: number = window.history.state?.tab;

    // ui
    const [tab, setTab] = React.useState(initTab ?? 1);
    const [studentResult, setStudentResult] = React.useState<StudentResult | undefined>(undefined);
    const [slideResult, setSlideResult] = React.useState<(SlideResult & { index: number }) | undefined>(undefined);

    // gql
    const { data: homework } = useQuery<GetHomeworkResponse, GetHomeworkVariables>(GET_HOMEWORK, { variables: { where: { id: homeworkId } } });

    const handleTabChange = (_event: React.SyntheticEvent, value: number) => {
        setTab(value);
        history.replaceState({ ...history.state, tab: value }, '');
    };

    const handleViewStudentResult = React.useCallback((result: StudentResult) => {
        setStudentResult(result);
    }, []);

    const handleViewSlideResult = React.useCallback((result: SlideResult & { index: number }) => {
        setSlideResult(result);
    }, []);

    return (
        <ResultsPageContainer>
            <ResultPageHeader name={homework?.homework.homeworkName} />
            <ResultsPageContent>
                <TabContext value={tab}>
                    <Box>
                        <TabList
                            sx={(theme) => ({
                                minHeight: 'auto',
                                px: 0.75,
                                pt: 0.75,
                                border: 1,
                                borderBottom: 0,
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                                borderColor: (theme.vars || theme).palette.divider,

                                '& .MuiTab-root': {
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.875rem',
                                },

                                '& .MuiTabs-flexContainer': {
                                    position: 'relative', // Add this for stable indicator positioning
                                },
                                '& .MuiTabs-indicator': {
                                    transition: 'none', // Remove transition for instant positioning
                                },
                            })}
                            onChange={handleTabChange}
                        >
                            <Tab label={t('result.label.summary')} value={0} icon={<LeaderboardIcon fontSize='small' />} iconPosition='start' />
                            <Tab
                                label={`${t('result.label.students')}` + (homework ? ` (${homework.homework.class.reduce((acc, c) => acc + c.studentsCount, 0).toString()})` : '')}
                                value={1}
                                icon={<FaceIcon fontSize='small' />}
                                iconPosition='start'
                            />
                            <Tab
                                label={`${t('result.label.slides')}` + (homework ? ` (${homework.homework.lessonVersion.slides.length.toString()})` : '')}
                                value={2}
                                icon={<NoteIcon fontSize='small' />}
                                iconPosition='start'
                            />
                        </TabList>
                    </Box>
                    <TabPanel value={0} sx={{ px: 0, py: 0, flex: 1 }}>
                        <SummaryTab homeworkId={homeworkId} />
                    </TabPanel>
                    <ResultTabPanel index={tab} value={1}>
                        <StudentTab homeworkId={homeworkId} onViewResult={handleViewStudentResult} />
                    </ResultTabPanel>
                    <ResultTabPanel index={tab} value={2}>
                        <SlideTab homeworkId={homeworkId} onViewResult={handleViewSlideResult} />
                    </ResultTabPanel>
                </TabContext>
            </ResultsPageContent>
            {studentResult && (
                <EditorProvider>
                    <StudentResultsDialog open={!!studentResult} onClose={() => setStudentResult(undefined)} result={studentResult} />
                </EditorProvider>
            )}
            {slideResult && (
                <EditorProvider>
                    <SlideResultsDialog open={!!slideResult} onClose={() => setSlideResult(undefined)} result={slideResult} />
                </EditorProvider>
            )}
        </ResultsPageContainer>
    );
}
